import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { hojeBrasilia, lerCentralDeDescontos, lerItensComDesconto } from '../../central';
import type {
  CentralDeDescontos,
  FiltroDeDescontos,
  ItemComDesconto,
  NumerosDeDesconto,
  OperadorDaRegra,
  OrdemDosProdutosComDesconto,
  RegraDeDesconto,
  ResumoDeDescontos,
  SituacaoDoItemVendido,
  VendedorDeDescontos,
} from '../../central';
import { baixarCsv } from '../../design/csv';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { fmt } from '../../design/graficos/modelos';
import { BarraDeFiltros, irPara, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import './central.css';

/**
 * PREÇOS & MARGEM — CENTRAL DE DESCONTOS e PERFORMANCE POR BALCONISTA (fase 1, dono 28/09/2026).
 *
 * Situação geral → onde está o problema → filtrar → investigar → abrir produto → abrir cupom.
 * Todos os filtros moram no endereço (compartilhar, voltar, recarregar) e mudam TODOS os números
 * da tela — o servidor calcula tudo sobre o mesmo conjunto de itens (vendas-hos/descontos.ts, onde
 * estão as fórmulas). A tela só mostra; não recalcula margem nem desconto.
 */

export type VisaoDaCentral = 'descontos' | 'balconistas';

const PRESETS = [5, 10, 20, 30, 50, 70, 80, 90];
const SITUACOES: readonly [SituacaoDoItemVendido, string][] = [
  ['com_desconto', 'Com desconto'],
  ['sem_desconto', 'Sem desconto'],
  ['abaixo_do_custo', 'Vendido abaixo do custo'],
  ['margem_negativa', 'Margem negativa'],
  ['margem_abaixo', 'Margem abaixo de X%'],
  ['sem_custo', 'Sem custo'],
];
const ORDENS: readonly [OrdemDosProdutosComDesconto, string][] = [
  ['desconto_rs', 'Maior desconto R$'],
  ['desconto_pct', 'Maior desconto %'],
  ['maior_desconto', 'Maior desconto num item'],
  ['faturamento', 'Maior faturamento'],
  ['quantidade', 'Mais vendidos'],
  ['lucro', 'Maior lucro'],
  ['margem', 'Menor margem'],
  ['abaixo_do_custo', 'Mais vendas abaixo do custo'],
];
const OPERADORES: readonly [OperadorDaRegra, string][] = [
  ['gt', 'acima de'],
  ['gte', 'a partir de'],
  ['lt', 'abaixo de'],
  ['entre', 'entre'],
];
const DIAS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

const num = (v: string | null) =>
  v === null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
const reais = (v: string | number | null) => (v === null ? '—' : fmt(Number(v), 'reais'));
const pctTxt = (v: number | null) => (v === null ? '—' : fmt(v, 'pct'));
const dataHora = (iso: string) =>
  new Date(iso)
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    })
    .replace(',', '');
const dataBr = (d: string) => d.split('-').reverse().join('/');

/** O filtro da central lido do endereço. */
export function lerFiltroDeDescontos(q: URLSearchParams): FiltroDeDescontos {
  let regras: RegraDeDesconto[] = [];
  try {
    const r = JSON.parse(q.get('regras') ?? '[]') as unknown;
    if (Array.isArray(r))
      regras = r.filter(
        (x): x is RegraDeDesconto =>
          typeof x === 'object' &&
          x !== null &&
          typeof (x as RegraDeDesconto).grupo === 'string' &&
          OPERADORES.some(([o]) => o === (x as RegraDeDesconto).op) &&
          typeof (x as RegraDeDesconto).a === 'number',
      );
  } catch {
    regras = [];
  }
  const sit = q.get('sit');
  return {
    grupos: (q.get('g') ?? '').split('|').filter(Boolean),
    vendedor: q.get('vend'),
    busca: q.get('busca'),
    fabricante: q.get('fab'),
    dmin: num(q.get('dmin')),
    dmax: num(q.get('dmax')),
    regras,
    situacao: SITUACOES.some(([s]) => s === sit) ? (sit as SituacaoDoItemVendido) : null,
    margemAbaixo: num(q.get('mab')),
    hde: num(q.get('hde')),
    hate: num(q.get('hate')),
    dias: (q.get('dias') ?? '')
      .split(',')
      .map(Number)
      .filter((d) => d >= 1 && d <= 7),
  };
}

/** O filtro de volta para o endereço (só o que está ligado). */
function extrasDoFiltro(f: FiltroDeDescontos): Record<string, string | null> {
  return {
    g: f.grupos.length ? f.grupos.join('|') : null,
    vend: f.vendedor,
    busca: f.busca,
    fab: f.fabricante,
    dmin: f.dmin === null ? null : String(f.dmin),
    dmax: f.dmax === null ? null : String(f.dmax),
    regras: f.regras.length ? JSON.stringify(f.regras) : null,
    sit: f.situacao,
    mab: f.margemAbaixo === null ? null : String(f.margemAbaixo),
    hde: f.hde === null ? null : String(f.hde),
    hate: f.hate === null ? null : String(f.hate),
    dias: f.dias.length ? f.dias.join(',') : null,
  };
}

const VAZIO: FiltroDeDescontos = {
  grupos: [],
  vendedor: null,
  busca: null,
  fabricante: null,
  dmin: null,
  dmax: null,
  regras: [],
  situacao: null,
  margemAbaixo: null,
  hde: null,
  hate: null,
  dias: [],
};

export function CentralDeDescontosTela({
  visao,
  parametros,
  veCusto,
}: {
  visao: VisaoDaCentral;
  parametros: URLSearchParams;
  veCusto: boolean;
}) {
  const hoje = hojeBrasilia();
  const q = new URLSearchParams(parametros);
  if (!q.get('p')) q.set('p', 'mes');
  const periodo = lerFiltros(q, hoje);
  const { de, ate } = periodo.periodo;
  const filtro = lerFiltroDeDescontos(parametros);
  const ordem = (
    ORDENS.some(([o]) => o === parametros.get('ordem')) ? parametros.get('ordem') : 'desconto_rs'
  ) as OrdemDosProdutosComDesconto;
  const pagina = Math.max(1, Number(parametros.get('pag') ?? 1) || 1);
  const chaveDoFiltro = JSON.stringify([filtro, ordem, pagina, de, ate]);

  const ir = (mudar: Partial<FiltroDeDescontos>, outros: Record<string, string | null> = {}) =>
    irPara({ filtro: periodo.filtro, periodo: periodo.periodo, pessoa: null }, 'precos', {
      visao,
      ...extrasDoFiltro({ ...filtro, ...mudar }),
      ordem: ordem === 'desconto_rs' ? null : ordem,
      ...outros,
    });

  const [d, setD] = useState<CentralDeDescontos | null>(null);
  const [erro, setErro] = useState(false);
  const [avancado, setAvancado] = useState(false);
  const [gaveta, setGaveta] = useState<{
    titulo: string;
    sub?: string;
    productId?: string | null;
    extra?: Partial<FiltroDeDescontos>;
    ordem?: 'recentes' | 'desconto_pct' | 'desconto_rs' | 'prejuizo';
  } | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerCentralDeDescontos(de, ate, filtro, { ordem, pagina }).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
    // o filtro inteiro está na chave (vem do endereço)
  }, [chaveDoFiltro]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(periodo.filtro, periodo.periodo, hoje);
  const nomeDoVendedor =
    filtro.vendedor && d
      ? (d.opcoes.vendedores.find((v) => v.chave === filtro.vendedor)?.nome ?? filtro.vendedor)
      : filtro.vendedor;
  const chips = chipsDoFiltro(filtro, nomeDoVendedor);
  const r = d?.resumo;

  return (
    <div className="cv-corpo vg dc">
      <BarraDeFiltros
        f={periodo}
        hoje={hoje}
        rota="precos"
        extras={{ visao, ...extrasDoFiltro(filtro), ordem: ordem === 'desconto_rs' ? null : ordem }}
      />

      <div className="dc-filtros">
        <div className="cv-segmento" role="group" aria-label="Desconto acima de">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={filtro.dmin === p && filtro.dmax === null ? 'cv-sel' : ''}
              onClick={() =>
                ir(
                  filtro.dmin === p && filtro.dmax === null
                    ? { dmin: null }
                    : { dmin: p, dmax: null },
                )
              }
            >
              &gt;{p}%
            </button>
          ))}
        </div>
        <label className="cv-select">
          Grupo
          <select
            value={filtro.grupos.length === 1 ? (filtro.grupos[0] ?? '') : ''}
            onChange={(e) => ir({ grupos: e.target.value ? [e.target.value] : [] })}
          >
            <option value="">
              {filtro.grupos.length > 1 ? `${String(filtro.grupos.length)} grupos` : 'Todos'}
            </option>
            {(d?.opcoes.grupos ?? []).map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <label className="cv-select">
          <Icone nome="users" tamanho={14} traco={1.6} />
          <select
            value={filtro.vendedor ?? ''}
            aria-label="Vendedor"
            onChange={(e) => ir({ vendedor: e.target.value || null })}
          >
            <option value="">Todos os vendedores</option>
            {(d?.opcoes.vendedores ?? []).map((v) => (
              <option key={v.chave} value={v.chave}>
                {v.nome}
              </option>
            ))}
          </select>
        </label>
        <Busca valor={filtro.busca ?? ''} aoBuscar={(b) => ir({ busca: b || null })} />
        <button
          type="button"
          className={`cv-botao ${avancado ? 'dc-ligado' : ''}`}
          aria-expanded={avancado}
          onClick={() => setAvancado((a) => !a)}
        >
          <Icone nome="filter" tamanho={14} traco={1.7} />{' '}
          {avancado ? 'Fechar filtros' : '+ Filtros avançados'}
        </button>
      </div>

      {avancado && d && (
        <FiltrosAvancados
          filtro={filtro}
          grupos={d.opcoes.grupos}
          fabricantes={d.opcoes.fabricantes}
          aoAplicar={(f) => {
            ir(f);
            setAvancado(false);
          }}
        />
      )}

      {chips.length > 0 && (
        <div className="dc-chips" aria-label="Filtros ativos">
          {chips.map((c) => (
            <button key={c.chave} type="button" className="dc-chip" onClick={() => ir(c.tirar)}>
              {c.texto} <Icone nome="x" tamanho={11} traco={2} />
            </button>
          ))}
          <button type="button" className="dc-limpar" onClick={() => ir(VAZIO)}>
            Limpar tudo
          </button>
        </div>
      )}

      {erro ? (
        <ErroDeArea oQue="a central de descontos" aoTentar={carregar} />
      ) : !d || !r ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : r.itens === 0 ? (
        <div className="cv-cartao dc-vazio">
          <h2>Nada encontrado</h2>
          <p>{textoDoVazio(filtro, nomeDoVendedor, quando)}</p>
        </div>
      ) : (
        <>
          {filtro.vendedor && d.resumoDaLoja && (
            <ComparacaoComALoja
              nome={nomeDoVendedor ?? ''}
              v={r}
              loja={d.resumoDaLoja}
              veCusto={veCusto}
            />
          )}

          <div className="dc-kpis">
            <Numero
              rotulo="VENDIDO"
              valor={reais(r.vendido)}
              sub={`${fmt(r.cupons, 'inteiro')} cupons · ${fmt(r.produtos, 'inteiro')} produtos · ${fmt(r.vendedores, 'inteiro')} vendedores`}
              dica="Soma do valor vendido (líquido) dos itens no filtro, sem canceladas."
            />
            <Numero
              rotulo="DESCONTO CONCEDIDO"
              valor={reais(r.descontoRs)}
              sub={`sobre ${reais(r.original)} de preço de tabela`}
              dica="Preço original − valor vendido. O preço original é o de tabela do HOS, derivado do desconto que o HOS informa: vendido ÷ (1 − desconto%)."
              destaque
              aoClicar={() =>
                setGaveta({
                  titulo: 'Itens com desconto',
                  ordem: 'desconto_rs',
                  extra: { situacao: 'com_desconto' },
                })
              }
            />
            <Numero
              rotulo="DESCONTO MÉDIO"
              valor={pctTxt(r.descontoPct)}
              sub={
                r.semDescontoConhecido
                  ? `${fmt(r.semDescontoConhecido, 'inteiro')} itens sem desconto informado`
                  : 'ponderado pelo valor'
              }
              dica="Ponderado pelo dinheiro: Σ desconto R$ ÷ Σ preço original. Nunca a média simples dos percentuais."
            />
            {veCusto && (
              <Numero
                rotulo="LUCRO BRUTO"
                valor={reais(r.lucro)}
                sub={
                  r.margem !== null
                    ? `margem ${pctTxt(r.margem)} · R$ ${String(r.lucroPorReal ?? 0).replace('.', ',')} por R$ 1`
                    : 'sem custo no período'
                }
                dica={`Valor vendido − custo do dia da venda, só nos itens com custo (${reais(r.vendidoComCusto)} vendidos). O custo por item só existe nas vendas da API do HOS${d.custoDesde ? `, desde ${dataBr(d.custoDesde)}` : ''}.`}
              />
            )}
            {veCusto && (
              <Numero
                rotulo="ABAIXO DO CUSTO"
                valor={`${fmt(r.abaixoDoCusto ?? 0, 'inteiro')} itens`}
                sub={`perda de ${reais(r.perdaAbaixoDoCusto)} · ${fmt(r.semCusto ?? 0, 'inteiro')} sem custo`}
                dica="Itens vendidos por menos que o custo do dia da venda. Clique para ver cada um."
                alerta={(r.abaixoDoCusto ?? 0) > 0}
                aoClicar={() =>
                  setGaveta({
                    titulo: 'Vendidos abaixo do custo',
                    ordem: 'prejuizo',
                    extra: { situacao: 'abaixo_do_custo' },
                  })
                }
              />
            )}
          </div>
          {veCusto && d.custoDesde && d.custoDesde > de && (
            <p className="dc-nota">
              <Icone nome="warn" tamanho={13} traco={1.7} /> Lucro e margem contam só a partir de{' '}
              {dataBr(d.custoDesde)}: antes disso as vendas vieram sem o custo do dia (migradas do
              1.0). Vendido e desconto valem para o período inteiro.
            </p>
          )}

          <div className="dc-linha-2">
            <PorGrupo
              linhas={d.porGrupo}
              veCusto={veCusto}
              aoEscolher={(g) => ir({ grupos: [g] })}
            />
            <PorFaixa
              linhas={d.porFaixa}
              aoEscolher={(de_, ate_) =>
                ir({
                  dmin: de_ === 0 && ate_ === 0 ? null : de_,
                  dmax: ate_,
                  situacao: de_ === 0 && ate_ === 0 ? 'sem_desconto' : filtro.situacao,
                })
              }
            />
          </div>

          {visao === 'balconistas' ? (
            <TabelaDeBalconistas
              linhas={d.porVendedor}
              loja={d.resumoDaLoja ?? r}
              veCusto={veCusto}
              selecionado={filtro.vendedor}
              aoEscolher={(v) => ir({ vendedor: v })}
            />
          ) : (
            <PorVendedor
              linhas={d.porVendedor}
              veCusto={veCusto}
              aoEscolher={(v) => ir({ vendedor: v })}
            />
          )}

          <Produtos
            d={d}
            veCusto={veCusto}
            ordem={ordem}
            aoOrdenar={(o) => ir({}, { ordem: o === 'desconto_rs' ? null : o })}
            aoPaginar={(p) =>
              ir(
                {},
                { ordem: ordem === 'desconto_rs' ? null : ordem, pag: p > 1 ? String(p) : null },
              )
            }
            aoAbrir={(p) =>
              setGaveta({
                titulo: p.produto,
                sub: [p.codigo, p.grupo, p.fabricante].filter(Boolean).join(' · '),
                productId: p.productId,
              })
            }
            aoExportar={() =>
              baixarCsv(`precos-descontos-${de}-a-${ate}.csv`, [
                ['Período', `${dataBr(de)} a ${dataBr(ate)}`],
                ['Filtros', chips.map((c) => c.texto).join(' · ') || 'nenhum'],
                [
                  'Gerado em',
                  new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
                ],
                [],
                [
                  'Código',
                  'Produto',
                  'Grupo',
                  'Fabricante',
                  'Qtd',
                  'Vendido',
                  'Preço de tabela',
                  'Desconto R$',
                  'Desconto %',
                  'Maior desconto %',
                  'Cupons',
                  ...(veCusto ? ['Custo', 'Lucro', 'Margem %', 'Abaixo do custo'] : []),
                ],
                ...d.produtos.linhas.map((p) => [
                  p.codigo,
                  p.produto,
                  p.grupo,
                  p.fabricante,
                  p.quantidade,
                  Number(p.vendido),
                  Number(p.original),
                  Number(p.descontoRs),
                  p.descontoPct,
                  p.maiorDescontoPct,
                  p.cupons,
                  ...(veCusto
                    ? [
                        p.custo === null ? null : Number(p.custo),
                        p.lucro === null ? null : Number(p.lucro),
                        p.margem,
                        p.abaixoDoCusto,
                      ]
                    : []),
                ]),
              ])
            }
          />
        </>
      )}

      {gaveta && (
        <GavetaDeItens
          de={de}
          ate={ate}
          filtro={{ ...filtro, ...(gaveta.extra ?? {}) }}
          productId={gaveta.productId ?? null}
          titulo={gaveta.titulo}
          sub={gaveta.sub}
          ordemInicial={gaveta.ordem ?? 'recentes'}
          veCusto={veCusto}
          aoFechar={() => setGaveta(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ peças */

function Busca({ valor, aoBuscar }: { valor: string; aoBuscar: (b: string) => void }) {
  const [t, setT] = useState(valor);
  useEffect(() => setT(valor), [valor]);
  return (
    <form
      className="cv-busca-nova"
      onSubmit={(e) => {
        e.preventDefault();
        aoBuscar(t.trim());
      }}
    >
      <Icone nome="search" tamanho={15} traco={1.7} />
      <input
        value={t}
        onChange={(e) => setT(e.target.value)}
        placeholder="Produto, código ou EAN"
        aria-label="Buscar produto, código ou EAN"
      />
    </form>
  );
}

function chipsDoFiltro(
  f: FiltroDeDescontos,
  nomeDoVendedor: string | null,
): { chave: string; texto: string; tirar: Partial<FiltroDeDescontos> }[] {
  const c: { chave: string; texto: string; tirar: Partial<FiltroDeDescontos> }[] = [];
  for (const g of f.grupos)
    c.push({ chave: `g:${g}`, texto: g, tirar: { grupos: f.grupos.filter((x) => x !== g) } });
  if (f.vendedor)
    c.push({ chave: 'vend', texto: nomeDoVendedor ?? f.vendedor, tirar: { vendedor: null } });
  if (f.busca) c.push({ chave: 'busca', texto: `"${f.busca}"`, tirar: { busca: null } });
  if (f.fabricante) c.push({ chave: 'fab', texto: f.fabricante, tirar: { fabricante: null } });
  if (f.dmin !== null || f.dmax !== null)
    c.push({
      chave: 'desc',
      texto:
        f.dmin !== null && f.dmax !== null
          ? `Desconto ${String(f.dmin)}–${String(f.dmax)}%`
          : f.dmin !== null
            ? `Desconto > ${String(f.dmin)}%`
            : `Desconto ≤ ${String(f.dmax)}%`,
      tirar: { dmin: null, dmax: null },
    });
  f.regras.forEach((r, k) =>
    c.push({
      chave: `r:${String(k)}`,
      texto: textoDaRegra(r),
      tirar: { regras: f.regras.filter((_, i) => i !== k) },
    }),
  );
  if (f.situacao)
    c.push({
      chave: 'sit',
      texto:
        f.situacao === 'margem_abaixo'
          ? `Margem < ${String(f.margemAbaixo ?? 10)}%`
          : (SITUACOES.find(([s]) => s === f.situacao)?.[1] ?? f.situacao),
      tirar: { situacao: null, margemAbaixo: null },
    });
  if (f.hde !== null || f.hate !== null)
    c.push({
      chave: 'hora',
      texto: `${String(f.hde ?? 0)}h às ${String(f.hate ?? 24)}h`,
      tirar: { hde: null, hate: null },
    });
  if (f.dias.length)
    c.push({
      chave: 'dias',
      texto: f.dias.map((d) => DIAS[d - 1]).join(', '),
      tirar: { dias: [] },
    });
  return c;
}

const textoDaRegra = (r: RegraDeDesconto) =>
  `${r.grupo} ${
    r.op === 'entre'
      ? `entre ${String(r.a)}% e ${String(r.b ?? 100)}%`
      : `${OPERADORES.find(([o]) => o === r.op)?.[1] ?? ''} ${String(r.a)}%`
  }`;

function textoDoVazio(f: FiltroDeDescontos, nomeDoVendedor: string | null, quando: string) {
  const partes: string[] = [];
  if (f.grupos.length) partes.push(`de ${f.grupos.join(', ')}`);
  if (f.dmin !== null) partes.push(`com desconto acima de ${String(f.dmin)}%`);
  if (f.regras.length)
    partes.push(`dentro das regras (${f.regras.map(textoDaRegra).join(' ou ')})`);
  if (nomeDoVendedor) partes.push(`vendida por ${nomeDoVendedor}`);
  if (f.situacao)
    partes.push(`na situação "${SITUACOES.find(([s]) => s === f.situacao)?.[1] ?? ''}"`);
  return `Nenhuma venda ${partes.join(' ') || 'com esses filtros'} foi encontrada — ${quando}.`;
}

function Numero({
  rotulo,
  valor,
  sub,
  dica,
  destaque,
  alerta,
  aoClicar,
}: {
  rotulo: string;
  valor: string;
  sub: string;
  dica: string;
  destaque?: boolean;
  alerta?: boolean;
  aoClicar?: () => void;
}) {
  const corpo = (
    <>
      <div className="cv-kpi-rotulo">
        {rotulo}{' '}
        <span className="dc-dica" title={dica} aria-label={dica}>
          ?
        </span>
      </div>
      <div className={`cv-kpi-valor ${alerta ? 'dc-alerta' : ''}`}>{valor}</div>
      <div className="cv-cinza cv-peq">{sub}</div>
    </>
  );
  return aoClicar ? (
    <button
      type="button"
      className={`cv-kpi dc-kpi-botao ${destaque ? 'dc-kpi-destaque' : ''}`}
      onClick={aoClicar}
    >
      {corpo}
    </button>
  ) : (
    <div className={`cv-kpi ${destaque ? 'dc-kpi-destaque' : ''}`}>{corpo}</div>
  );
}

/** Barras horizontais clicáveis (o mesmo desenho para grupo, faixa e vendedor). */
function Barras({
  linhas,
  aoEscolher,
}: {
  linhas: readonly {
    chave: string;
    nome: string;
    valor: number;
    texto: string;
    detalhe: string;
    dica: string;
  }[];
  aoEscolher: (chave: string) => void;
}) {
  const max = Math.max(1e-9, ...linhas.map((l) => Math.abs(l.valor)));
  return (
    <ul className="dc-barras">
      {linhas.map((l) => (
        <li key={l.chave}>
          <button type="button" onClick={() => aoEscolher(l.chave)} title={l.dica}>
            <span className="dc-barra-nome">{l.nome}</span>
            <span className="dc-barra">
              <i style={{ width: `${String((Math.abs(l.valor) / max) * 100)}%` }} />
            </span>
            <b>{l.texto}</b>
            <em className="cv-cinza cv-peq">{l.detalhe}</em>
          </button>
        </li>
      ))}
    </ul>
  );
}

const dicaDe = (n: NumerosDeDesconto, veCusto: boolean) =>
  [
    `Vendido: ${reais(n.vendido)} (${fmt(n.cupons, 'inteiro')} cupons, ${fmt(n.quantidade, 'inteiro')} un.)`,
    `Preço de tabela: ${reais(n.original)}`,
    `Desconto: ${reais(n.descontoRs)} · ${pctTxt(n.descontoPct)}`,
    ...(veCusto
      ? [
          `Lucro: ${reais(n.lucro)} · margem ${pctTxt(n.margem)}`,
          `Abaixo do custo: ${String(n.abaixoDoCusto ?? 0)}`,
        ]
      : []),
  ].join('\n');

function Cartao({
  titulo,
  dica,
  direita,
  children,
}: {
  titulo: string;
  dica?: string;
  direita?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="cv-cartao">
      <div className="cv-cartao-topo">
        <h2>
          {titulo}
          {dica && (
            <span className="dc-dica" title={dica} aria-label={dica}>
              ?
            </span>
          )}
        </h2>
        {direita}
      </div>
      {children}
    </div>
  );
}

function Alternar<T extends string>({
  valor,
  opcoes,
  aoMudar,
}: {
  valor: T;
  opcoes: readonly [T, string][];
  aoMudar: (v: T) => void;
}) {
  return (
    <div className="cv-segmento dc-alternar" role="tablist">
      {opcoes.map(([v, r]) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={valor === v}
          className={valor === v ? 'cv-sel' : ''}
          onClick={() => aoMudar(v)}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function PorGrupo({
  linhas,
  veCusto,
  aoEscolher,
}: {
  linhas: CentralDeDescontos['porGrupo'];
  veCusto: boolean;
  aoEscolher: (g: string) => void;
}) {
  const [m, setM] = useState<'pct' | 'rs' | 'fat'>('pct');
  const valor = (g: NumerosDeDesconto) =>
    m === 'pct' ? (g.descontoPct ?? 0) : m === 'rs' ? Number(g.descontoRs) : Number(g.vendido);
  const ord = [...linhas].sort((a, b) => valor(b) - valor(a));
  return (
    <Cartao
      titulo="Desconto por grupo"
      dica="Desconto ponderado de cada grupo. Clique no grupo para filtrar a página inteira."
      direita={
        <Alternar
          valor={m}
          opcoes={[
            ['pct', '%'],
            ['rs', 'R$'],
            ['fat', 'Vendido'],
          ]}
          aoMudar={setM}
        />
      }
    >
      <Barras
        linhas={ord.map((g) => ({
          chave: g.grupo,
          nome: g.grupo,
          valor: valor(g),
          texto:
            m === 'pct'
              ? pctTxt(g.descontoPct)
              : m === 'rs'
                ? reais(g.descontoRs)
                : reais(g.vendido),
          detalhe: m === 'pct' ? reais(g.descontoRs) : pctTxt(g.descontoPct),
          dica: dicaDe(g, veCusto),
        }))}
        aoEscolher={aoEscolher}
      />
    </Cartao>
  );
}

function PorFaixa({
  linhas,
  aoEscolher,
}: {
  linhas: CentralDeDescontos['porFaixa'];
  aoEscolher: (de: number, ate: number | null) => void;
}) {
  return (
    <Cartao
      titulo="Vendido por faixa de desconto"
      dica="Quanto foi vendido em cada faixa de desconto do item. Clique na faixa para filtrar."
    >
      <Barras
        linhas={linhas.map((f) => ({
          chave: f.faixa,
          nome: f.faixa,
          valor: Number(f.vendido),
          texto: reais(f.vendido),
          detalhe: `${fmt(f.produtos, 'inteiro')} prod. · −${reais(f.descontoRs)}`,
          dica: `${f.faixa}\n${String(f.itens)} itens · ${String(f.cupons)} cupons\nDesconto: ${reais(f.descontoRs)}${f.margem !== null ? `\nMargem: ${pctTxt(f.margem)}` : ''}`,
        }))}
        aoEscolher={(k) => {
          const f = linhas.find((x) => x.faixa === k);
          if (f) aoEscolher(f.de, f.ate);
        }}
      />
    </Cartao>
  );
}

function PorVendedor({
  linhas,
  veCusto,
  aoEscolher,
}: {
  linhas: readonly VendedorDeDescontos[];
  veCusto: boolean;
  aoEscolher: (chave: string) => void;
}) {
  const [m, setM] = useState<'pct' | 'rs' | 'lucro' | 'cupons'>('pct');
  const valor = (v: VendedorDeDescontos) =>
    m === 'pct'
      ? (v.descontoPct ?? 0)
      : m === 'rs'
        ? Number(v.descontoRs)
        : m === 'lucro'
          ? Number(v.lucro ?? 0)
          : v.cupons;
  const top = [...linhas].sort((a, b) => valor(b) - valor(a)).slice(0, 10);
  return (
    <Cartao
      titulo="Desconto por vendedor · top 10"
      dica="Clique no vendedor para ver a página inteira só com as vendas dele."
      direita={
        <Alternar
          valor={m}
          opcoes={[
            ['pct', 'Desconto %'],
            ['rs', 'Desconto R$'],
            ...(veCusto ? ([['lucro', 'Lucro']] as [typeof m, string][]) : []),
            ['cupons', 'Cupons'],
          ]}
          aoMudar={setM}
        />
      }
    >
      <Barras
        linhas={top.map((v) => ({
          chave: v.chave,
          nome: v.nome,
          valor: valor(v),
          texto:
            m === 'pct'
              ? pctTxt(v.descontoPct)
              : m === 'rs'
                ? reais(v.descontoRs)
                : m === 'lucro'
                  ? reais(v.lucro)
                  : fmt(v.cupons, 'inteiro'),
          detalhe: `${reais(v.vendido)} vendidos`,
          dica: `${v.nome}\n${dicaDe(v, veCusto)}`,
        }))}
        aoEscolher={aoEscolher}
      />
    </Cartao>
  );
}

type ColunaDoBalconista =
  | 'vendido'
  | 'descontoRs'
  | 'descontoPct'
  | 'lucro'
  | 'margem'
  | 'lucroPorReal'
  | 'cupons'
  | 'ticket'
  | 'quantidade'
  | 'abaixo';

function TabelaDeBalconistas({
  linhas,
  loja,
  veCusto,
  selecionado,
  aoEscolher,
}: {
  linhas: readonly VendedorDeDescontos[];
  loja: ResumoDeDescontos;
  veCusto: boolean;
  selecionado: string | null;
  aoEscolher: (chave: string | null) => void;
}) {
  const [ord, setOrd] = useState<{ c: ColunaDoBalconista; asc: boolean }>({
    c: 'vendido',
    asc: false,
  });
  const val = (v: VendedorDeDescontos, c: ColunaDoBalconista): number => {
    switch (c) {
      case 'vendido':
        return Number(v.vendido);
      case 'descontoRs':
        return Number(v.descontoRs);
      case 'descontoPct':
        return v.descontoPct ?? -1;
      case 'lucro':
        return Number(v.lucro ?? -Infinity);
      case 'margem':
        return v.margem ?? -Infinity;
      case 'lucroPorReal':
        return v.lucroPorReal ?? -Infinity;
      case 'cupons':
        return v.cupons;
      case 'ticket':
        return Number(v.ticketMedio ?? 0);
      case 'quantidade':
        return v.quantidade;
      case 'abaixo':
        return v.abaixoDoCusto ?? 0;
    }
  };
  const ordenadas = [...linhas].sort(
    (a, b) => (ord.asc ? 1 : -1) * (val(a, ord.c) - val(b, ord.c)),
  );
  const Th = ({
    c,
    children,
    dica,
  }: {
    c: ColunaDoBalconista;
    children: ReactNode;
    dica?: string;
  }) => (
    <th className="cv-dir" title={dica}>
      <button
        type="button"
        className={`dc-ordenar ${ord.c === c ? 'dc-ordenar-sel' : ''}`}
        onClick={() => setOrd((o) => ({ c, asc: o.c === c ? !o.asc : false }))}
      >
        {children}
        {ord.c === c ? (ord.asc ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
  return (
    <Cartao
      titulo={`Performance por balconista · ${String(linhas.length)}`}
      dica="Faturamento sozinho não é resultado: compare lucro, margem e desconto. Clique no nome para abrir as vendas dele na página inteira."
    >
      <div className="cv-rolagem">
        <table data-sem-ordenar className="cv-tabela dc-tabela">
          <thead>
            <tr>
              <th>Balconista</th>
              <Th c="vendido" dica="Valor vendido (líquido)">
                Vendido
              </Th>
              <Th c="descontoRs">Desconto R$</Th>
              <Th c="descontoPct" dica="Ponderado pelo valor">
                Desconto %
              </Th>
              {veCusto && (
                <Th c="lucro" dica="Só itens com custo do dia">
                  Lucro
                </Th>
              )}
              {veCusto && <Th c="margem">Margem</Th>}
              {veCusto && (
                <Th c="lucroPorReal" dica="Lucro ÷ valor vendido (itens com custo)">
                  Lucro/R$ 1
                </Th>
              )}
              <Th c="cupons">Cupons</Th>
              <Th c="ticket">Ticket</Th>
              <Th c="quantidade">Itens</Th>
              {veCusto && <Th c="abaixo">Abaixo custo</Th>}
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((v) => (
              <tr
                key={v.chave}
                className={`dc-clicavel ${selecionado === v.chave ? 'dc-linha-sel' : ''}`}
                tabIndex={0}
                onClick={() => aoEscolher(selecionado === v.chave ? null : v.chave)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && aoEscolher(selecionado === v.chave ? null : v.chave)
                }
              >
                <td className="cv-forte">{v.nome}</td>
                <td className="cv-dir">{reais(v.vendido)}</td>
                <td className="cv-dir">{reais(v.descontoRs)}</td>
                <td className="cv-dir">{pctTxt(v.descontoPct)}</td>
                {veCusto && <td className="cv-dir cv-forte">{reais(v.lucro)}</td>}
                {veCusto && <td className="cv-dir">{pctTxt(v.margem)}</td>}
                {veCusto && (
                  <td className="cv-dir">
                    {v.lucroPorReal === null ? '—' : fmt(v.lucroPorReal, 'reais')}
                  </td>
                )}
                <td className="cv-dir">{fmt(v.cupons, 'inteiro')}</td>
                <td className="cv-dir">{reais(v.ticketMedio)}</td>
                <td className="cv-dir">{fmt(v.quantidade, 'inteiro')}</td>
                {veCusto && (
                  <td className={`cv-dir ${(v.abaixoDoCusto ?? 0) > 0 ? 'dc-alerta' : ''}`}>
                    {fmt(v.abaixoDoCusto ?? 0, 'inteiro')}
                  </td>
                )}
              </tr>
            ))}
            <tr className="dc-total">
              <td>Loja</td>
              <td className="cv-dir">{reais(loja.vendido)}</td>
              <td className="cv-dir">{reais(loja.descontoRs)}</td>
              <td className="cv-dir">{pctTxt(loja.descontoPct)}</td>
              {veCusto && <td className="cv-dir">{reais(loja.lucro)}</td>}
              {veCusto && <td className="cv-dir">{pctTxt(loja.margem)}</td>}
              {veCusto && (
                <td className="cv-dir">
                  {loja.lucroPorReal === null ? '—' : fmt(loja.lucroPorReal, 'reais')}
                </td>
              )}
              <td className="cv-dir">{fmt(loja.cupons, 'inteiro')}</td>
              <td className="cv-dir">
                {loja.cupons ? reais(Number(loja.vendido) / loja.cupons) : '—'}
              </td>
              <td className="cv-dir">{fmt(loja.quantidade, 'inteiro')}</td>
              {veCusto && <td className="cv-dir">{fmt(loja.abaixoDoCusto ?? 0, 'inteiro')}</td>}
            </tr>
          </tbody>
        </table>
      </div>
    </Cartao>
  );
}

function ComparacaoComALoja({
  nome,
  v,
  loja,
  veCusto,
}: {
  nome: string;
  v: ResumoDeDescontos;
  loja: ResumoDeDescontos;
  veCusto: boolean;
}) {
  const ticket = (x: ResumoDeDescontos) => (x.cupons ? Number(x.vendido) / x.cupons : null);
  const pp = (a: number | null, b: number | null) =>
    a === null || b === null ? '—' : `${a - b >= 0 ? '+' : ''}${fmt(a - b, 'decimal')} p.p.`;
  const itens: { rotulo: string; dele: string; daLoja: string; dif: string }[] = [
    {
      rotulo: 'Participação no vendido',
      dele: reais(v.vendido),
      daLoja: reais(loja.vendido),
      dif:
        Number(loja.vendido) > 0 ? pctTxt((Number(v.vendido) / Number(loja.vendido)) * 100) : '—',
    },
    {
      rotulo: 'Desconto médio',
      dele: pctTxt(v.descontoPct),
      daLoja: pctTxt(loja.descontoPct),
      dif: pp(v.descontoPct, loja.descontoPct),
    },
    ...(veCusto
      ? [
          {
            rotulo: 'Margem',
            dele: pctTxt(v.margem),
            daLoja: pctTxt(loja.margem),
            dif: pp(v.margem, loja.margem),
          },
          {
            rotulo: 'Lucro por R$ 1',
            dele: v.lucroPorReal === null ? '—' : fmt(v.lucroPorReal, 'reais'),
            daLoja: loja.lucroPorReal === null ? '—' : fmt(loja.lucroPorReal, 'reais'),
            dif: '',
          },
        ]
      : []),
    {
      rotulo: 'Ticket médio',
      dele: reais(ticket(v)),
      daLoja: reais(ticket(loja)),
      dif:
        ticket(v) !== null && ticket(loja)
          ? `${(ticket(v) ?? 0) >= (ticket(loja) ?? 0) ? '+' : ''}${reais((ticket(v) ?? 0) - (ticket(loja) ?? 0))}`
          : '—',
    },
  ];
  return (
    <div className="cv-cartao dc-comparacao">
      <h2>
        Performance — {nome}{' '}
        <span className="cv-cinza gr-normal">× média da loja com os mesmos filtros</span>
      </h2>
      <div className="dc-comp-grade">
        {itens.map((i) => (
          <div key={i.rotulo}>
            <span className="cv-kpi-rotulo">{i.rotulo.toUpperCase()}</span>
            <b>{i.dele}</b>
            <em className="cv-cinza cv-peq">
              loja {i.daLoja}
              {i.dif ? ` · ${i.dif}` : ''}
            </em>
          </div>
        ))}
      </div>
    </div>
  );
}

function Produtos({
  d,
  veCusto,
  ordem,
  aoOrdenar,
  aoPaginar,
  aoAbrir,
  aoExportar,
}: {
  d: CentralDeDescontos;
  veCusto: boolean;
  ordem: OrdemDosProdutosComDesconto;
  aoOrdenar: (o: OrdemDosProdutosComDesconto) => void;
  aoPaginar: (p: number) => void;
  aoAbrir: (p: CentralDeDescontos['produtos']['linhas'][number]) => void;
  aoExportar: () => void;
}) {
  const { linhas, total, pagina, porPagina } = d.produtos;
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  return (
    <div className="cv-cartao cv-sem-pad">
      <div className="cv-cartao-topo cv-pad">
        <h2>Produtos · {fmt(total, 'inteiro')}</h2>
        <span className="dc-acoes">
          <label className="cv-select">
            Ordenar
            <select
              value={ordem}
              onChange={(e) => aoOrdenar(e.target.value as OrdemDosProdutosComDesconto)}
            >
              {ORDENS.filter(
                ([o]) => veCusto || !['lucro', 'margem', 'abaixo_do_custo'].includes(o),
              ).map(([o, r]) => (
                <option key={o} value={o}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="cv-botao" onClick={aoExportar}>
            <Icone nome="receipt" tamanho={14} traco={1.7} /> Exportar página
          </button>
        </span>
      </div>
      <div className="cv-rolagem">
        <table data-sem-ordenar className="cv-tabela dc-tabela">
          <thead>
            <tr>
              <th>Produto</th>
              <th className="cv-dir">Qtd</th>
              <th className="cv-dir">Vendido</th>
              <th className="cv-dir">Desconto R$</th>
              <th className="cv-dir" title="Ponderado pelo valor">
                Desconto %
              </th>
              <th className="cv-dir" title="O maior desconto dado num item">
                Maior
              </th>
              <th className="cv-dir">Cupons</th>
              {veCusto && <th className="cv-dir">Lucro</th>}
              {veCusto && <th className="cv-dir">Margem</th>}
              {veCusto && <th className="cv-dir">Abaixo custo</th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((p) => (
              <tr
                key={p.productId}
                className="dc-clicavel"
                tabIndex={0}
                onClick={() => aoAbrir(p)}
                onKeyDown={(e) => e.key === 'Enter' && aoAbrir(p)}
              >
                <td>
                  <div className="cv-forte">{p.produto}</div>
                  <div className="cv-mono cv-cinza cv-peq">
                    {[p.codigo, p.grupo, p.fabricante].filter(Boolean).join(' · ')}
                  </div>
                </td>
                <td className="cv-dir">{fmt(p.quantidade, 'inteiro')}</td>
                <td className="cv-dir">{reais(p.vendido)}</td>
                <td className="cv-dir cv-forte">{reais(p.descontoRs)}</td>
                <td className="cv-dir">{pctTxt(p.descontoPct)}</td>
                <td className="cv-dir">{pctTxt(p.maiorDescontoPct)}</td>
                <td className="cv-dir">{fmt(p.cupons, 'inteiro')}</td>
                {veCusto && (
                  <td className={`cv-dir ${Number(p.lucro ?? 0) < 0 ? 'cv-neg' : ''}`}>
                    {reais(p.lucro)}
                  </td>
                )}
                {veCusto && (
                  <td className={`cv-dir ${(p.margem ?? 0) < 0 ? 'cv-neg' : ''}`}>
                    {pctTxt(p.margem)}
                  </td>
                )}
                {veCusto && (
                  <td className={`cv-dir ${(p.abaixoDoCusto ?? 0) > 0 ? 'dc-alerta' : ''}`}>
                    {fmt(p.abaixoDoCusto ?? 0, 'inteiro')}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginas > 1 && (
        <div className="cv-pad dc-paginas">
          <button
            type="button"
            className="cv-botao"
            disabled={pagina <= 1}
            onClick={() => aoPaginar(pagina - 1)}
          >
            Anterior
          </button>
          <span className="cv-cinza cv-peq">
            página {pagina} de {paginas}
          </span>
          <button
            type="button"
            className="cv-botao"
            disabled={pagina >= paginas}
            onClick={() => aoPaginar(pagina + 1)}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

function FiltrosAvancados({
  filtro,
  grupos,
  fabricantes,
  aoAplicar,
}: {
  filtro: FiltroDeDescontos;
  grupos: readonly string[];
  fabricantes: readonly string[];
  aoAplicar: (f: Partial<FiltroDeDescontos>) => void;
}) {
  const [regras, setRegras] = useState<RegraDeDesconto[]>(
    filtro.regras.length ? [...filtro.regras] : [],
  );
  const [dmin, setDmin] = useState(filtro.dmin === null ? '' : String(filtro.dmin));
  const [dmax, setDmax] = useState(filtro.dmax === null ? '' : String(filtro.dmax));
  const [sit, setSit] = useState<SituacaoDoItemVendido | ''>(filtro.situacao ?? '');
  const [mab, setMab] = useState(filtro.margemAbaixo === null ? '10' : String(filtro.margemAbaixo));
  const [fab, setFab] = useState(filtro.fabricante ?? '');
  const [hde, setHde] = useState(filtro.hde === null ? '' : String(filtro.hde));
  const [hate, setHate] = useState(filtro.hate === null ? '' : String(filtro.hate));
  const [dias, setDias] = useState<number[]>([...filtro.dias]);
  const [gs, setGs] = useState<string[]>([...filtro.grupos]);
  const n = (t: string) =>
    t.trim() === '' ? null : Math.max(0, Math.min(100, Number(t.replace(',', '.'))));
  const h = (t: string, max: number) =>
    t.trim() === '' ? null : Math.max(0, Math.min(max, Math.floor(Number(t))));
  const regrasValidas = regras.filter((r) => r.grupo && Number.isFinite(r.a));
  const muda = (k: number, m: Partial<RegraDeDesconto>) =>
    setRegras((rs) => rs.map((r, i) => (i === k ? { ...r, ...m } : r)));

  return (
    <div className="cv-cartao dc-avancado">
      <section>
        <h3>Regra de desconto por grupo</h3>
        <p className="cv-cinza cv-peq">
          Cada grupo com o seu limite. Várias regras juntas valem com <b>OU</b> (ex.: GENÉRICOS
          acima de 70% ou MEDICAMENTOS acima de 10%).
        </p>
        {regras.map((r, k) => (
          <div key={k} className="dc-regra">
            <select
              value={r.grupo}
              onChange={(e) => muda(k, { grupo: e.target.value })}
              aria-label="Grupo"
            >
              <option value="">Grupo…</option>
              {grupos.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <select
              value={r.op}
              onChange={(e) => muda(k, { op: e.target.value as OperadorDaRegra })}
              aria-label="Condição"
            >
              {OPERADORES.map(([o, t]) => (
                <option key={o} value={o}>
                  {t}
                </option>
              ))}
            </select>
            <input
              inputMode="decimal"
              value={String(r.a)}
              onChange={(e) => muda(k, { a: n(e.target.value) ?? 0 })}
              aria-label="Valor"
            />
            %
            {r.op === 'entre' && (
              <>
                e
                <input
                  inputMode="decimal"
                  value={String(r.b ?? 100)}
                  onChange={(e) => muda(k, { b: n(e.target.value) ?? 100 })}
                  aria-label="Até"
                />
                %
              </>
            )}
            <button
              type="button"
              className="dc-x"
              aria-label="Tirar regra"
              onClick={() => setRegras((rs) => rs.filter((_, i) => i !== k))}
            >
              <Icone nome="x" tamanho={12} traco={2} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="cv-botao"
          onClick={() => setRegras((rs) => [...rs, { grupo: '', op: 'gt', a: 10 }])}
        >
          + Adicionar grupo
        </button>
      </section>

      <section>
        <h3>Desconto do item</h3>
        <div className="dc-par">
          de{' '}
          <input
            inputMode="decimal"
            value={dmin}
            onChange={(e) => setDmin(e.target.value)}
            aria-label="Desconto acima de"
          />{' '}
          % até{' '}
          <input
            inputMode="decimal"
            value={dmax}
            onChange={(e) => setDmax(e.target.value)}
            aria-label="Desconto até"
          />{' '}
          %
        </div>
        <h3>Grupos</h3>
        <div className="dc-marcas">
          {grupos.map((g) => (
            <label key={g}>
              <input
                type="checkbox"
                checked={gs.includes(g)}
                onChange={(e) =>
                  setGs((x) => (e.target.checked ? [...x, g] : x.filter((y) => y !== g)))
                }
              />
              {g}
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3>Situação e margem</h3>
        <select
          value={sit}
          onChange={(e) => setSit(e.target.value as SituacaoDoItemVendido | '')}
          aria-label="Situação"
        >
          <option value="">Todas</option>
          {SITUACOES.map(([s, t]) => (
            <option key={s} value={s}>
              {t}
            </option>
          ))}
        </select>
        {sit === 'margem_abaixo' && (
          <div className="dc-par">
            margem abaixo de{' '}
            <input
              inputMode="decimal"
              value={mab}
              onChange={(e) => setMab(e.target.value)}
              aria-label="Margem abaixo de"
            />{' '}
            %
          </div>
        )}
        <h3>Fabricante</h3>
        <select value={fab} onChange={(e) => setFab(e.target.value)} aria-label="Fabricante">
          <option value="">Todos</option>
          {fabricantes.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </section>

      <section>
        <h3>Horário e dia</h3>
        <div className="dc-par">
          das{' '}
          <input
            inputMode="numeric"
            value={hde}
            onChange={(e) => setHde(e.target.value)}
            aria-label="Hora inicial"
          />{' '}
          h às{' '}
          <input
            inputMode="numeric"
            value={hate}
            onChange={(e) => setHate(e.target.value)}
            aria-label="Hora final"
          />{' '}
          h
        </div>
        <div className="dc-marcas">
          {DIAS.map((t, k) => (
            <label key={t}>
              <input
                type="checkbox"
                checked={dias.includes(k + 1)}
                onChange={(e) =>
                  setDias((x) => (e.target.checked ? [...x, k + 1] : x.filter((y) => y !== k + 1)))
                }
              />
              {t}
            </label>
          ))}
        </div>
      </section>

      <div className="dc-avancado-acoes">
        <button
          type="button"
          className="cv-botao-primario"
          onClick={() =>
            aoAplicar({
              regras: regrasValidas,
              dmin: n(dmin),
              dmax: n(dmax),
              situacao: sit || null,
              margemAbaixo: sit === 'margem_abaixo' ? Number(mab.replace(',', '.')) || 10 : null,
              fabricante: fab || null,
              hde: h(hde, 23),
              hate: h(hate, 24),
              dias: [...dias].sort(),
              grupos: gs,
            })
          }
        >
          Aplicar filtros
        </button>
      </div>
    </div>
  );
}

function GavetaDeItens({
  de,
  ate,
  filtro,
  productId,
  titulo,
  sub,
  ordemInicial,
  veCusto,
  aoFechar,
}: {
  de: string;
  ate: string;
  filtro: FiltroDeDescontos;
  productId: string | null;
  titulo: string;
  sub?: string | undefined;
  ordemInicial: 'recentes' | 'desconto_pct' | 'desconto_rs' | 'prejuizo';
  veCusto: boolean;
  aoFechar: () => void;
}) {
  const [ordem, setOrdem] = useState(ordemInicial);
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState<{
    linhas: readonly ItemComDesconto[];
    total: number;
    porPagina: number;
  } | null>(null);
  const [erro, setErro] = useState(false);
  const chave = useMemo(
    () => JSON.stringify([filtro, productId, ordem, pagina]),
    [filtro, productId, ordem, pagina],
  );
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerItensComDesconto(de, ate, filtro, { productId, ordem, pagina }).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
  }, [chave]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);
  const paginas = d ? Math.max(1, Math.ceil(d.total / d.porPagina)) : 1;
  return (
    <div className="cv-veu" onClick={aoFechar}>
      <aside
        className="cv-gaveta dc-gaveta"
        role="dialog"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div className="cv-gaveta-selos">
            <span className="gr-tag">VENDAS</span>
            <span className="cv-cinza cv-peq">
              {dataBr(de)} a {dataBr(ate)} · com os filtros da página
            </span>
          </div>
          <button type="button" className="cv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="x" tamanho={15} traco={1.8} />
          </button>
          <h2>{titulo}</h2>
          {sub && <p>{sub}</p>}
        </header>
        <div className="cv-gaveta-corpo">
          <div className="dc-gaveta-topo">
            <span className="cv-cinza cv-peq">
              {d ? `${fmt(d.total, 'inteiro')} itens vendidos` : ''}
            </span>
            <label className="cv-select">
              Ordem
              <select
                value={ordem}
                onChange={(e) => {
                  setOrdem(e.target.value as typeof ordem);
                  setPagina(1);
                }}
              >
                <option value="recentes">Mais recentes</option>
                <option value="desconto_pct">Maior desconto %</option>
                <option value="desconto_rs">Maior desconto R$</option>
                {veCusto && <option value="prejuizo">Maior prejuízo</option>}
              </select>
            </label>
          </div>
          {erro ? (
            <ErroDeArea oQue="as vendas" aoTentar={carregar} />
          ) : !d ? (
            <div className="cv-carregando" aria-busy="true" />
          ) : d.linhas.length === 0 ? (
            <p className="cv-cinza">Nenhuma venda com esses filtros.</p>
          ) : (
            <div className="cv-rolagem">
              <table className="cv-tabela dc-tabela">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cupom</th>
                    {!productId && <th>Produto</th>}
                    <th>Vendedor</th>
                    <th className="cv-dir">Qtd</th>
                    <th className="cv-dir" title="Preço de tabela do HOS, derivado do desconto">
                      Original
                    </th>
                    <th className="cv-dir">Vendido</th>
                    <th className="cv-dir">Desc.</th>
                    <th className="cv-dir">Desc. R$</th>
                    {veCusto && <th className="cv-dir">Custo</th>}
                    {veCusto && <th className="cv-dir">Lucro</th>}
                  </tr>
                </thead>
                <tbody>
                  {d.linhas.map((l) => (
                    <tr key={l.itemId} className={l.abaixoDoCusto ? 'dc-linha-alerta' : ''}>
                      <td className="cv-mono">{dataHora(l.em)}</td>
                      <td>
                        <a
                          className="cv-mono"
                          href={`#/cupom?id=${l.saleId}&destaque=${l.productId}`}
                        >
                          {l.cupom}
                        </a>
                      </td>
                      {!productId && (
                        <td>
                          <div className="cv-forte">{l.produto}</div>
                          <div className="cv-cinza cv-peq">{l.grupo}</div>
                        </td>
                      )}
                      <td>{l.vendedor ?? <span className="cv-cinza">—</span>}</td>
                      <td className="cv-dir">{fmt(l.quantidade, 'inteiro')}</td>
                      <td className="cv-dir cv-cinza">{reais(l.precoOriginal)}</td>
                      <td className="cv-dir">{reais(l.precoVendido)}</td>
                      <td className="cv-dir cv-forte">{pctTxt(l.descontoPct)}</td>
                      <td className="cv-dir">{reais(l.descontoRs)}</td>
                      {veCusto && <td className="cv-dir cv-cinza">{reais(l.custo)}</td>}
                      {veCusto && (
                        <td className={`cv-dir ${Number(l.lucro ?? 0) < 0 ? 'cv-neg' : ''}`}>
                          {reais(l.lucro)}
                          {l.margem !== null && (
                            <em className="cv-cinza cv-peq"> {pctTxt(l.margem)}</em>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {d && paginas > 1 && (
            <div className="dc-paginas">
              <button
                type="button"
                className="cv-botao"
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => p - 1)}
              >
                Anterior
              </button>
              <span className="cv-cinza cv-peq">
                página {pagina} de {paginas}
              </span>
              <button
                type="button"
                className="cv-botao"
                disabled={pagina >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
